<template>
  <div class="pdf-zoom">
    <button :disabled="isDownDisabled" @click="decrementScale" class="pdf-zoom__button">
      <BaseIcon name="minus" size="13" />
    </button>
    <div class="pdf-zoom__number">
      <input
        ref="zoomInput"
        class="pdf-zoom__input"
        v-model="currentScale"
        type="number"
        @change="setFreeScale()"
        @keydown.enter="onEnter"
      />
      <span class="pdf-zoom__percent">%</span>
    </div>
    <button :disabled="isUpDisabled" @click="incrementScale" class="pdf-zoom__button">
      <BaseIcon name="plus" size="13" />
    </button>
    <button
      @click="fitToPage"
      class="pdf-zoom__button pdf-zoom__button--fit"
      v-tooltip="{ content: $t('pdf-viewer.fit-to-page'), placement: 'bottom' }"
    >
      <BaseIcon name="fit-page" size="13" />
    </button>
  </div>
</template>

<script>
import { bus } from '@/main';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'PDFZoom',
  components: {
    BaseIcon,
  },
  data() {
    return {
      currentScale: null,
      currentScaleIndex: null,
    };
  },
  computed: {
    ...mapGetters('pdfData', ['pdfScale', 'pdfScaleValues']),
    isUpDisabled() {
      return this.currentScaleIndex === this.pdfScaleValues.length - 1;
    },
    isDownDisabled() {
      return this.currentScaleIndex === 0;
    },
  },
  methods: {
    ...mapActions('pdfData', ['setPdfScale']),
    findNearestScaleIndex(scale) {
      let nearestIndex = 0;
      let minDiff = Math.abs(this.pdfScaleValues[0] - scale);

      for (let i = 1; i < this.pdfScaleValues.length; i++) {
        const diff = Math.abs(this.pdfScaleValues[i] - scale);
        if (diff < minDiff) {
          minDiff = diff;
          nearestIndex = i;
        }
      }
      return nearestIndex;
    },
    incrementScale() {
      const nextIndex = this.pdfScaleValues.findIndex((v) => v > this.currentScale);
      if (nextIndex !== -1) {
        this.setPdfScale(this.pdfScaleValues[nextIndex]);
      }
    },
    decrementScale() {
      let prevIndex = -1;
      for (let i = this.pdfScaleValues.length - 1; i >= 0; i--) {
        if (this.pdfScaleValues[i] < this.currentScale) {
          prevIndex = i;
          break;
        }
      }
      if (prevIndex !== -1) {
        this.setPdfScale(this.pdfScaleValues[prevIndex]);
      }
    },
    setFreeScale() {
      this.setPdfScale(this.currentScale);
    },
    onEnter() {
      this.setFreeScale();
      this.$refs.zoomInput.blur();
    },
    fitToPage() {
      bus.$emit('set-pdf-zoom-mode', 'page-fit');
    },
  },
  watch: {
    pdfScale() {
      this.currentScale = this.pdfScale;
      this.currentScaleIndex = this.findNearestScaleIndex(this.pdfScale);
    },
  },
  created() {
    this.currentScale = this.pdfScale;
    this.currentScaleIndex = this.pdfScaleValues.indexOf(this.pdfScale);
  },
  destroyed() {
    this.setPdfScale(100);
  },
};
</script>

<style lang="scss">
.pdf-zoom {
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    outline: none;
    padding: 0;
    color: $color-normal;
    transition: color 150ms ease;

    .base-icon,
    .feather {
      height: 13px;
      width: 13px;
    }

    &:hover:not(:disabled) {
      color: $color-accent;
    }

    &:disabled {
      color: $color-neutral;
      cursor: not-allowed;
    }

    &--fit {
      margin-left: 8px;
    }
  }

  &__number {
    display: flex;
    align-items: center;
    justify-content: center;
    background: $color-neutral-pale;
    border-radius: $border-radius-small;
    height: 20px;
    padding: 0 8px;
  }

  &__input {
    background: transparent;
    border: none;
    color: $color-normal;
    font-size: 12px;
    font-family: $font-families;
    outline: none;
    padding: 0;
    text-align: center;
    width: 24px;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      appearance: none;
      margin: 0;
    }

    &[type='number'] {
      -moz-appearance: textfield;
      appearance: textfield;
      width: 24px;
    }
  }

  &__percent {
    font-size: 12px;
    font-family: $font-families;
    color: $color-normal;
  }
}
</style>
